import { useEffect, useRef, useState, useCallback } from 'react';

interface BarcodeScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onDetected: (codigo: string) => void;
}

/**
 * Scanner de código de barras / QR Code via câmera.
 * Usa a BarcodeDetector API (nativa em Chrome/Android) quando disponível.
 * Fallback: permite ao usuário capturar foto e tenta extrair via canvas.
 */
export default function BarcodeScanner({ isOpen, onClose, onDetected }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number>(0);
  const detectorRef = useRef<BarcodeDetector | null>(null);

  const [status, setStatus] = useState<'iniciando' | 'ativo' | 'erro' | 'sem-suporte'>('iniciando');
  const [erroMsg, setErroMsg] = useState('');
  const [detectado, setDetectado] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  // Verifica suporte à BarcodeDetector API
  const temBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  const pararCamera = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const iniciarCamera = useCallback(async () => {
    setStatus('iniciando');
    setErroMsg('');
    setDetectado(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStatus('ativo');

        // Inicializa BarcodeDetector se disponível
        if (temBarcodeDetector) {
          detectorRef.current = new BarcodeDetector({
            formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'itf', 'qr_code', 'data_matrix', 'pdf417'],
          });
          iniciarDeteccao();
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('Permission') || msg.includes('NotAllowed')) {
        setErroMsg('Permissão de câmera negada. Permita o acesso nas configurações do navegador.');
      } else if (msg.includes('NotFound') || msg.includes('DevicesNotFound')) {
        setErroMsg('Nenhuma câmera encontrada neste dispositivo.');
      } else {
        setErroMsg('Não foi possível acessar a câmera. Tente digitar o código manualmente.');
      }
      setStatus('erro');
    }
  }, [facingMode, temBarcodeDetector]);

  const iniciarDeteccao = useCallback(() => {
    if (!detectorRef.current || !videoRef.current) return;

    const detectar = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) {
        animFrameRef.current = requestAnimationFrame(detectar);
        return;
      }

      try {
        const barcodes = await detectorRef.current!.detect(videoRef.current);
        if (barcodes.length > 0) {
          const codigo = barcodes[0].rawValue;
          if (codigo && codigo.length >= 10) {
            setDetectado(true);
            pararCamera();
            setTimeout(() => {
              onDetected(codigo.replace(/\D/g, ''));
              onClose();
            }, 400);
            return;
          }
        }
      } catch {
        // Ignora erros de frame
      }

      animFrameRef.current = requestAnimationFrame(detectar);
    };

    animFrameRef.current = requestAnimationFrame(detectar);
  }, [onDetected, onClose, pararCamera]);

  // Captura foto manual (fallback)
  const capturarFoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    // Sem BarcodeDetector: tenta via canvas (limitado)
    // Informa ao usuário para digitar manualmente
    setErroMsg('Não foi possível detectar automaticamente. Por favor, cole o código no campo de texto.');
    setStatus('erro');
    pararCamera();
  }, [pararCamera]);

  const trocarCamera = useCallback(() => {
    pararCamera();
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, [pararCamera]);

  useEffect(() => {
    if (isOpen) {
      iniciarCamera();
    } else {
      pararCamera();
    }
    return () => pararCamera();
  }, [isOpen, iniciarCamera, pararCamera]);

  // Re-inicia quando troca câmera
  useEffect(() => {
    if (isOpen && status !== 'iniciando') {
      iniciarCamera();
    }
  }, [facingMode]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-[60] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg">
            <i className="ri-qr-scan-2-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-white text-sm font-bold">Escanear Código</p>
            <p className="text-white/60 text-xs">Aponte para o código de barras ou QR Code</p>
          </div>
        </div>
        <button
          onClick={() => { pararCamera(); onClose(); }}
          className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-white text-xl"></i>
        </button>
      </div>

      {/* Viewfinder */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* Overlay com guia de scan */}
        {status === 'ativo' && !detectado && (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Escurecimento nas bordas */}
            <div className="absolute inset-0 bg-black/40"></div>

            {/* Janela de scan */}
            <div className="relative z-10 w-72 h-40">
              {/* Cantos animados */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-teal-400 rounded-tl-lg"></div>
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-teal-400 rounded-tr-lg"></div>
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-teal-400 rounded-bl-lg"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-teal-400 rounded-br-lg"></div>

              {/* Linha de scan animada */}
              <div className="absolute inset-x-2 h-0.5 bg-teal-400/80 animate-[scanLine_2s_ease-in-out_infinite]"
                style={{ top: '50%', boxShadow: '0 0 8px rgba(45,212,191,0.8)' }}
              ></div>

              {/* Área transparente */}
              <div className="absolute inset-0 bg-transparent border border-teal-400/30 rounded-sm"></div>
            </div>

            {/* Instrução */}
            <div className="absolute bottom-16 left-0 right-0 text-center">
              <p className="text-white text-sm font-medium bg-black/50 mx-8 py-2 px-4 rounded-full">
                {temBarcodeDetector
                  ? 'Posicione o código dentro da área'
                  : 'Posicione e toque em Capturar'}
              </p>
            </div>
          </div>
        )}

        {/* Detectado! */}
        {detectado && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="bg-teal-500 rounded-2xl p-6 text-center">
              <i className="ri-checkbox-circle-fill text-5xl text-white mb-2"></i>
              <p className="text-white font-bold text-lg">Código detectado!</p>
            </div>
          </div>
        )}

        {/* Iniciando */}
        {status === 'iniciando' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <div className="text-center">
              <i className="ri-loader-4-line animate-spin text-4xl text-teal-400 mb-3"></i>
              <p className="text-white text-sm">Iniciando câmera...</p>
            </div>
          </div>
        )}

        {/* Erro */}
        {status === 'erro' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6">
            <div className="bg-white rounded-2xl p-6 text-center max-w-xs">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-camera-off-line text-2xl text-red-500"></i>
              </div>
              <p className="text-slate-800 font-semibold text-sm mb-2">Câmera indisponível</p>
              <p className="text-slate-500 text-xs mb-4">{erroMsg}</p>
              <button
                onClick={() => { pararCamera(); onClose(); }}
                className="w-full py-2.5 bg-teal-500 text-white rounded-xl text-sm font-semibold cursor-pointer"
              >
                Digitar manualmente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Controles inferiores */}
      {status === 'ativo' && !detectado && (
        <div className="flex items-center justify-between px-6 py-4 bg-black/80 flex-shrink-0">
          {/* Trocar câmera */}
          <button
            onClick={trocarCamera}
            className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer"
            title="Trocar câmera"
          >
            <i className="ri-camera-switch-line text-white text-xl"></i>
          </button>

          {/* Capturar (fallback sem BarcodeDetector) */}
          {!temBarcodeDetector && (
            <button
              onClick={capturarFoto}
              className="w-16 h-16 flex items-center justify-center bg-teal-500 rounded-full hover:bg-teal-600 transition-colors cursor-pointer border-4 border-white"
            >
              <i className="ri-camera-line text-white text-2xl"></i>
            </button>
          )}

          {/* Indicador de detecção automática */}
          {temBarcodeDetector && (
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
              <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></div>
              <span className="text-white text-xs">Detecção automática</span>
            </div>
          )}

          {/* Fechar */}
          <button
            onClick={() => { pararCamera(); onClose(); }}
            className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
        </div>
      )}

      {/* Animação da linha de scan */}
      <style>{`
        @keyframes scanLine {
          0%, 100% { transform: translateY(-60px); opacity: 0.6; }
          50% { transform: translateY(60px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Declaração de tipo para BarcodeDetector (não está no TS padrão)
declare class BarcodeDetector {
  constructor(options?: { formats?: string[] });
  detect(image: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement): Promise<Array<{ rawValue: string; format: string }>>;
  static getSupportedFormats(): Promise<string[]>;
}
